import React, { useEffect, useState } from 'react';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../auth/useAuth';
import { useGameStore } from '../../store/gameStore';
import { Achievement, AchievementMetric } from '../../types';
import { trackWrite } from '../../services/firebaseUsageTracker';
import type { AdminConfig } from '../AdminPanelModal';

const FieldLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <label className="block text-sm font-semibold text-gray-200 mb-1.5">{children}</label>
);

const HelperText: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <p className="text-xs text-gray-400 mt-1">{children}</p>
);

type AchievementRequirement = {
  metric: AchievementMetric;
  value: number;
  comparator: 'gte' | 'lte' | 'eq';
};

type LevelStage = {
  level: number;
  title: string;
};

type LevelingConfig = {
  baseXP: number;
  increment: number;
  maxLevel: number;
  stages: LevelStage[];
};

const DEFAULT_LEVELING: LevelingConfig = {
  baseXP: 100,
  increment: 25,
  maxLevel: 50,
  stages: [
    { level: 1, title: 'Newcomer' },
    { level: 5, title: 'Beginner' },
    { level: 10, title: 'Novice' },
    { level: 15, title: 'Apprentice' },
    { level: 20, title: 'Card Counter' },
    { level: 25, title: 'Skilled Player' },
    { level: 30, title: 'Professional' },
    { level: 35, title: 'Expert Counter' },
    { level: 40, title: 'Master Counter' },
    { level: 45, title: 'Legendary Counter' },
  ]
};

const DEFAULT_EMOJI_OPTIONS = ['🎯', '🏆', '⭐', '🔥', '💎', '🚀', '🧠', '💰', '⚡', '📈', '🎰'];

const METRIC_OPTIONS = [
  { id: 'handsPlayed', label: 'Hands Played' },
  { id: 'handsWon', label: 'Hands Won' },
  { id: 'blackjacks', label: 'Blackjacks' },
  { id: 'perfectBets', label: 'Perfect Bets' },
  { id: 'winRate', label: 'Win Rate %' },
  { id: 'bestStreak', label: 'Best Streak' },
  { id: 'countAccuracy', label: 'Count Accuracy %' },
  { id: 'betSizingAccuracy', label: 'Bet Accuracy %' },
  { id: 'splitHandsPlayed', label: 'Split Hands' },
  { id: 'scenariosAttempted', label: 'Scenarios Attempted' },
  { id: 'scenariosCorrect', label: 'Scenarios Correct' },
  { id: 'deviationCorrect', label: 'Deviation Correct' },
  { id: 'insuranceCorrect', label: 'Insurance Correct' },
  { id: 'speedDrillHighScore', label: 'Speed Drill High Score' },
  { id: 'currentBankroll', label: 'Current Bankroll' },
  { id: 'totalXP', label: 'Total XP' },
];

type Props = {
  config: AdminConfig | null;
  configLoading: boolean;
  onConfigUpdate: (updates: Partial<AdminConfig>) => void;
};

export const LevelingAchievementsSettings: React.FC<Props> = ({ config, configLoading, onConfigUpdate }) => {
  const { roles, profile } = useAuth();
  const { checkAchievements } = useGameStore();
  const isAdmin = roles.includes('admin');
  const isDev = roles.includes('dev');

  // Local state derived from cached config
  const [leveling, setLeveling] = useState<LevelingConfig>(DEFAULT_LEVELING);
  const [customAchievements, setCustomAchievements] = useState<Achievement[]>([]);
  const [emojiOptions, setEmojiOptions] = useState<string[]>(DEFAULT_EMOJI_OPTIONS);
  
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [activeSection, setActiveSection] = useState<'leveling' | 'achievements'>('leveling');
  const [newStage, setNewStage] = useState({ level: 50, title: '' });
  const [newAchievement, setNewAchievement] = useState<{
    name: string;
    description: string;
    requirement: number;
    metric: AchievementMetric;
    rewardXP: number;
    icon: string;
    iconType: 'emoji';
    requirements?: AchievementRequirement[];
  }>({
    name: '',
    description: '',
    requirement: 1,
    metric: 'handsPlayed',
    rewardXP: 25,
    icon: '🎯',
    iconType: 'emoji'
  });

  // Sync local state with cached config when it loads
  useEffect(() => {
    if (config) {
      setLeveling(config.leveling || DEFAULT_LEVELING);
      setCustomAchievements((config.customAchievements || []) as Achievement[]);
      setEmojiOptions(config.achievementEmojiOptions || DEFAULT_EMOJI_OPTIONS);
    }
  }, [config]);

  // Update newStage level when leveling stages change
  useEffect(() => {
    const lastLevel = leveling.stages?.slice(-1)?.[0]?.level || 45;
    setNewStage(prev => ({ ...prev, level: lastLevel + 5 }));
  }, [leveling.stages]);

  if (!isAdmin && !isDev) {
    return <div className="text-center py-8 text-gray-400">Only admins can configure leveling and achievements.</div>;
  }

  if (configLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500"></div>
      </div>
    );
  }

  const saveConfig = async (updates: Partial<AdminConfig>) => {
    if (!profile?.uid) return;
    
    setSaving(true);
    setMessage(null);
    
    try {
      const configRef = doc(db, 'adminConfigs', profile.uid);
      await setDoc(configRef, updates, { merge: true });
      trackWrite('adminConfigs', 'save-config');
      
      // Update parent cache
      onConfigUpdate(updates);
      
      setMessage({ type: 'success', text: 'Settings saved!' });
    } catch (err) {
      console.error('Failed to save config:', err);
      setMessage({ type: 'error', text: err instanceof Error ? err.message : 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  const updateLeveling = (partial: Partial<LevelingConfig>) => {
    const next = { ...leveling, ...partial };
    setLeveling(next);
    saveConfig({ leveling: next });
  };

  const slugify = (text: string) =>
    text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '') || `custom-${Date.now()}`;

  const handleAddStage = () => {
    if (!newStage.title.trim()) return;
    const stages = [...(leveling.stages || []), { ...newStage }];
    updateLeveling({ stages });
    setNewStage({ level: newStage.level + 5, title: '' });
  };

  const handleStageChange = (index: number, field: 'level' | 'title', value: string | number) => {
    const stages = (leveling.stages || []).map((stage, idx) =>
      idx === index ? { ...stage, [field]: field === 'level' ? Number(value) : value } : stage
    );
    updateLeveling({ stages });
  };

  const handleRemoveStage = (index: number) => {
    const stages = (leveling.stages || []).filter((_, idx) => idx !== index);
    updateLeveling({ stages });
  };

  const handleAchievementChange = (index: number, field: string, value: unknown) => {
    const custom = [...customAchievements];
    const next: Achievement = { ...custom[index], [field]: field === 'metric' ? (value as AchievementMetric) : value };
    next.category = 'custom';
    next.isCustom = true;
    custom[index] = next;
    setCustomAchievements(custom);
    saveConfig({ customAchievements: custom });
  };

  const handleDeleteAchievement = (index: number) => {
    const custom = [...customAchievements];
    custom.splice(index, 1);
    setCustomAchievements(custom);
    saveConfig({ customAchievements: custom });
  };

  const handleAddAchievement = () => {
    if (!newAchievement.name.trim()) return;
    const id = slugify(newAchievement.name);
    const customAchievement: Achievement = {
      id,
      ...newAchievement,
      isCustom: true,
      category: 'custom',
    };
    const custom = [...customAchievements, customAchievement];
    setCustomAchievements(custom);
    saveConfig({ customAchievements: custom });
    setNewAchievement({
      ...newAchievement,
      name: '',
      description: '',
      requirement: 1,
      rewardXP: 25,
      icon: emojiOptions[0] || '🎯',
    });
  };

  return (
    <div className="space-y-6">
      {message && (
        <div className={`p-3 rounded-lg text-sm ${
          message.type === 'success' 
            ? 'bg-emerald-900/50 border border-emerald-700 text-emerald-200' 
            : 'bg-red-900/50 border border-red-700 text-red-200'
        }`}>
          {message.text}
        </div>
      )}

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-gray-700 pb-2">
        <button
          onClick={() => setActiveSection('leveling')}
          className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors ${
            activeSection === 'leveling'
              ? 'bg-gray-800 text-white border-b-2 border-emerald-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          XP & Leveling
        </button>
        <button
          onClick={() => setActiveSection('achievements')}
          className={`px-4 py-2 rounded-t-lg text-sm font-semibold transition-colors ${
            activeSection === 'achievements'
              ? 'bg-gray-800 text-white border-b-2 border-emerald-500'
              : 'text-gray-400 hover:text-white'
          }`}
        >
          Custom Achievements
        </button>
      </div>

      {activeSection === 'leveling' && (
        <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-white mb-1">XP & Leveling Configuration</h3>
            <p className="text-sm text-gray-400">
              Control XP growth and stage titles. These settings propagate to all your invited users.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <FieldLabel>Base XP (Level 1 ➜ 2)</FieldLabel>
              <input
                type="number"
                min={10}
                value={leveling.baseXP}
                onChange={(e) => updateLeveling({ baseXP: Math.max(1, parseInt(e.target.value) || 0) })}
                disabled={saving}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
              />
            </div>
            <div>
              <FieldLabel>Increment per Level</FieldLabel>
              <input
                type="number"
                min={0}
                value={leveling.increment}
                onChange={(e) => updateLeveling({ increment: Math.max(0, parseInt(e.target.value) || 0) })}
                disabled={saving}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
              />
            </div>
            <div>
              <FieldLabel>Max Level</FieldLabel>
              <input
                type="number"
                min={1}
                value={leveling.maxLevel}
                onChange={(e) => updateLeveling({ maxLevel: Math.max(1, parseInt(e.target.value) || 1) })}
                disabled={saving}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
              />
            </div>
          </div>

          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <FieldLabel>Level Titles</FieldLabel>
              <span className="text-xs text-gray-400">Shown on Progress page</span>
            </div>
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {(leveling.stages || []).map((stage, idx) => (
                <div key={`${stage.level}-${idx}`} className="flex flex-col sm:flex-row sm:items-center gap-2">
                  <div className="flex items-center gap-2 sm:flex-1">
                    <input
                      type="number"
                      min={1}
                      value={stage.level}
                      onChange={(e) => handleStageChange(idx, 'level', parseInt(e.target.value) || 1)}
                      disabled={saving}
                      className="w-20 sm:w-24 px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                    />
                    <input
                      type="text"
                      value={stage.title}
                      onChange={(e) => handleStageChange(idx, 'title', e.target.value)}
                      disabled={saving}
                      className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                      placeholder="Stage title"
                    />
                  </div>
                  <button
                    onClick={() => handleRemoveStage(idx)}
                    disabled={saving}
                    className="px-3 py-2 rounded-lg bg-red-700/70 hover:bg-red-700 text-white text-sm w-full sm:w-auto disabled:opacity-50"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-2 border-t border-gray-700 pt-3">
              <div className="flex items-center gap-2 sm:flex-1">
                <input
                  type="number"
                  min={1}
                  value={newStage.level}
                  onChange={(e) => setNewStage({ ...newStage, level: Math.max(1, parseInt(e.target.value) || 1) })}
                  disabled={saving}
                  className="w-20 sm:w-24 px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                  placeholder="Level"
                />
                <input
                  type="text"
                  value={newStage.title}
                  onChange={(e) => setNewStage({ ...newStage, title: e.target.value })}
                  disabled={saving}
                  className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                  placeholder="Title (e.g., Legendary Counter)"
                />
              </div>
              <button
                onClick={handleAddStage}
                disabled={saving}
                className="px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm w-full sm:w-auto disabled:opacity-50"
              >
                Add Stage
              </button>
            </div>
          </div>
        </div>
      )}

      {activeSection === 'achievements' && (
        <div className="bg-gray-800/60 border border-gray-700 rounded-xl p-4 space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white mb-1">Custom Achievements</h3>
              <p className="text-sm text-gray-400">
                Add your own milestones. These propagate to all your invited users.
              </p>
            </div>
            <button
              onClick={() => checkAchievements()}
              className="px-3 py-2 rounded-lg bg-gray-700 border border-gray-600 text-sm text-gray-200 hover:bg-gray-600"
            >
              Refresh Achievements
            </button>
          </div>

          <div className="space-y-3 max-h-96 overflow-y-auto">
            {customAchievements.length === 0 && (
              <div className="text-sm text-gray-400 py-4 text-center">No custom achievements yet. Add one below.</div>
            )}
            {customAchievements.map((ach, idx) => (
              <div key={ach.id} className="p-3 rounded-lg border border-gray-700 bg-gray-900/40 space-y-2">
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <FieldLabel>Name</FieldLabel>
                    <input
                      type="text"
                      value={ach.name}
                      onChange={(e) => handleAchievementChange(idx, 'name', e.target.value)}
                      disabled={saving}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <FieldLabel>Emoji</FieldLabel>
                    <div className="flex gap-2">
                      <select
                        value={ach.icon}
                        onChange={(e) => handleAchievementChange(idx, 'icon', e.target.value)}
                        disabled={saving}
                        className="px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                      >
                        {emojiOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                      </select>
                      <input
                        type="text"
                        value={ach.icon}
                        maxLength={4}
                        onChange={(e) => handleAchievementChange(idx, 'icon', e.target.value)}
                        disabled={saving}
                        className="w-24 px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                        placeholder="Emoji"
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <FieldLabel>Description</FieldLabel>
                  <input
                    type="text"
                    value={ach.description}
                    onChange={(e) => handleAchievementChange(idx, 'description', e.target.value)}
                    disabled={saving}
                    className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                  />
                </div>
                <div className="grid md:grid-cols-4 gap-3">
                  <div>
                    <FieldLabel>Requirement</FieldLabel>
                    <input
                      type="number"
                      min={1}
                      value={ach.requirement}
                      onChange={(e) => handleAchievementChange(idx, 'requirement', Math.max(1, parseInt(e.target.value) || 1))}
                      disabled={saving}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                    />
                  </div>
                  <div>
                    <FieldLabel>Metric</FieldLabel>
                    <select
                      value={ach.metric || 'handsPlayed'}
                      onChange={(e) => handleAchievementChange(idx, 'metric', e.target.value)}
                      disabled={saving}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                    >
                      {METRIC_OPTIONS.map(opt => (
                        <option key={opt.id} value={opt.id}>{opt.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <FieldLabel>XP Reward</FieldLabel>
                    <input
                      type="number"
                      min={0}
                      value={ach.rewardXP || 0}
                      onChange={(e) => handleAchievementChange(idx, 'rewardXP', Math.max(0, parseInt(e.target.value) || 0))}
                      disabled={saving}
                      className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                    />
                  </div>
                  <div className="flex items-end">
                    <button
                      onClick={() => handleDeleteAchievement(idx)}
                      disabled={saving}
                      className="w-full px-3 py-2 rounded-lg bg-red-700/70 hover:bg-red-700 text-white text-sm disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
                {/* Multi-criteria editor */}
                <div className="space-y-2 border-t border-gray-700 pt-2 mt-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-gray-300">Additional Requirements (all must pass)</span>
                    <button
                      onClick={() => {
                        const next = [...(ach.requirements || [])];
                        next.push({ metric: 'handsPlayed', value: 1, comparator: 'gte' });
                        handleAchievementChange(idx, 'requirements', next);
                      }}
                      disabled={saving}
                      className="px-2 py-1 rounded bg-gray-800 border border-gray-700 text-xs text-gray-200 hover:bg-gray-700 disabled:opacity-50"
                    >
                      Add Requirement
                    </button>
                  </div>
                  {(ach.requirements || []).length === 0 && (
                    <div className="text-xs text-gray-500">No extra requirements.</div>
                  )}
                  {(ach.requirements || []).map((req, rIdx) => (
                    <div key={rIdx} className="grid grid-cols-12 gap-2 items-center">
                      <select
                        value={req.metric}
                        onChange={(e) => {
                          const next = [...(ach.requirements || [])];
                          next[rIdx] = { ...next[rIdx], metric: e.target.value as AchievementMetric };
                          handleAchievementChange(idx, 'requirements', next);
                        }}
                        disabled={saving}
                        className="col-span-5 px-2 py-2 bg-gray-900 border border-gray-700 text-white rounded focus:border-emerald-500 text-sm disabled:opacity-50"
                      >
                        {METRIC_OPTIONS.map(opt => (
                          <option key={opt.id} value={opt.id}>{opt.label}</option>
                        ))}
                      </select>
                      <select
                        value={req.comparator || 'gte'}
                        onChange={(e) => {
                          const next = [...(ach.requirements || [])];
                          next[rIdx] = { ...next[rIdx], comparator: e.target.value as 'gte' | 'lte' | 'eq' };
                          handleAchievementChange(idx, 'requirements', next);
                        }}
                        disabled={saving}
                        className="col-span-2 px-2 py-2 bg-gray-900 border border-gray-700 text-white rounded focus:border-emerald-500 text-sm disabled:opacity-50"
                      >
                        <option value="gte">≥</option>
                        <option value="lte">≤</option>
                        <option value="eq">=</option>
                      </select>
                      <input
                        type="number"
                        min={0}
                        value={req.value}
                        onChange={(e) => {
                          const next = [...(ach.requirements || [])];
                          next[rIdx] = { ...next[rIdx], value: parseFloat(e.target.value) || 0 };
                          handleAchievementChange(idx, 'requirements', next);
                        }}
                        disabled={saving}
                        className="col-span-3 px-2 py-2 bg-gray-900 border border-gray-700 text-white rounded focus:border-emerald-500 text-sm disabled:opacity-50"
                      />
                      <button
                        onClick={() => {
                          const next = [...(ach.requirements || [])];
                          next.splice(rIdx, 1);
                          handleAchievementChange(idx, 'requirements', next);
                        }}
                        disabled={saving}
                        className="col-span-2 px-2 py-2 rounded bg-red-700/70 hover:bg-red-700 text-white text-xs disabled:opacity-50"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* Add new achievement form */}
          <div className="border border-gray-700 rounded-lg p-3 bg-gray-900/40 space-y-2">
            <div className="font-semibold text-white text-sm">Add new achievement</div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <FieldLabel>Name</FieldLabel>
                <input
                  type="text"
                  value={newAchievement.name}
                  onChange={(e) => setNewAchievement({ ...newAchievement, name: e.target.value })}
                  disabled={saving}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                  placeholder="e.g., Risk Champion"
                />
              </div>
              <div>
                <FieldLabel>Emoji</FieldLabel>
                <div className="flex gap-2">
                  <select
                    value={newAchievement.icon}
                    onChange={(e) => setNewAchievement({ ...newAchievement, icon: e.target.value })}
                    disabled={saving}
                    className="px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                  >
                    {emojiOptions.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                  <input
                    type="text"
                    value={newAchievement.icon}
                    maxLength={4}
                    onChange={(e) => setNewAchievement({ ...newAchievement, icon: e.target.value })}
                    disabled={saving}
                    className="w-20 px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                    placeholder="Emoji"
                  />
                </div>
              </div>
            </div>
            <div>
              <FieldLabel>Description</FieldLabel>
              <input
                type="text"
                value={newAchievement.description}
                onChange={(e) => setNewAchievement({ ...newAchievement, description: e.target.value })}
                disabled={saving}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                placeholder="What unlocks this?"
              />
            </div>
            <div className="grid md:grid-cols-3 gap-3">
              <div>
                <FieldLabel>Requirement</FieldLabel>
                <input
                  type="number"
                  min={1}
                  value={newAchievement.requirement}
                  onChange={(e) => setNewAchievement({ ...newAchievement, requirement: Math.max(1, parseInt(e.target.value) || 1) })}
                  disabled={saving}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                />
              </div>
              <div>
                <FieldLabel>Metric</FieldLabel>
                <select
                  value={newAchievement.metric}
                  onChange={(e) => setNewAchievement({ ...newAchievement, metric: e.target.value as AchievementMetric })}
                  disabled={saving}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                >
                  {METRIC_OPTIONS.map(opt => (
                    <option key={opt.id} value={opt.id}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <FieldLabel>XP Reward</FieldLabel>
                <input
                  type="number"
                  min={0}
                  value={newAchievement.rewardXP}
                  onChange={(e) => setNewAchievement({ ...newAchievement, rewardXP: Math.max(0, parseInt(e.target.value) || 0) })}
                  disabled={saving}
                  className="w-full px-3 py-2 bg-gray-900 border border-gray-700 text-white rounded-lg focus:border-emerald-500 disabled:opacity-50"
                />
              </div>
            </div>
            <div className="flex justify-end pt-2">
              <button
                onClick={handleAddAchievement}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-sm disabled:opacity-50"
              >
                Add Achievement
              </button>
            </div>
          </div>
          
          <HelperText>
            Note: Accuracy-based achievements (80%+, 90%+, 95%+) automatically require minimum hands played (25, 50, 100 respectively) to prevent false unlocks.
          </HelperText>
        </div>
      )}

      {saving && (
        <div className="text-center text-sm text-gray-400">
          Saving...
        </div>
      )}
    </div>
  );
};
